import dayjs from 'dayjs';
import { startSpan as sentryStartSpan, captureException } from '@sentry/browser';
import { getFormattedTime } from '../date';
const formatTemplate = en ? 'dddd, MM/DD' : 'dddd[, der ]DD.MM.';

/**
 * resets a select Input
 * @param {HTMLElement} select
 */
export const resetSelect = select => {
  const firstOption = select.querySelector('option:first-child');
  select.innerHTML = '';
  select.add(firstOption);
  select.value = '';
};

/**
 * fills the selects with appointments
 * @param {*} data
 * @param {HTMLElement} dateSelect
 * @param {HTMLElement} timeSelect
 * @param {boolean} noDayOption
 */
// Sentry tracing helper to wrap calendar network calls without breaking older browsers.
const startSpan = (name, op, attributes, cb) => {
  return sentryStartSpan ? sentryStartSpan({ name, op, attributes }, cb) : cb();
};

export const fillAppointment = (data, dateSelect, timeSelect, noDayOption) => {
  const start = data.result.map(e => e.start);
  const end = data.result.map(e => e.end);
  const startGroup = start.reduce((acc, date) => {
    const [_, d, t] = date.match(/(.+)T(.+)/);
    acc[d] = [...(acc[d] || []), t];
    return acc;
  }, {});

  for (const key in startGroup) {
    const option = document.createElement('option');

    option.text = dayjs(key).format(formatTemplate);
    option.value = key;

    dateSelect.add(option);
  }

  if (noDayOption) {
    const noDay = document.createElement('option');
    noDay.text = en ? 'Call me back' : 'Rückruf anfordern';
    noDay.value = 'noday';
    noDay.id = 'noDayOption';
    dateSelect.add(noDay);
  }

  dateSelect.addEventListener('change', e => {
    resetSelect(timeSelect);
    const dateSelectValue = dateSelect.value;

    if (dateSelectValue === 'noday') {
      const noDayTime = document.createElement('option');
      noDayTime.text = en ? "We'll call you." : 'Wir melden uns.';
      noDayTime.value = 'noday';
      noDayTime.selected = true;
      timeSelect.add(noDayTime);
    }

    if (dateSelect.value !== '' && dateSelectValue !== 'noday') {
      if (!startGroup[dateSelectValue]?.length) {
        return;
      }
      for (const time of startGroup[dateSelectValue]) {
        const option = document.createElement('option');
        const date = new Date(dateSelectValue + 'T' + time);

        option.text = getFormattedTime(date);
        option.value = start.indexOf(dateSelectValue + 'T' + time);
        option.dataset.start = start[option.value];
        option.dataset.end = end[option.value];

        timeSelect.add(option);
      }

      timeSelect.disabled = false;
      timeSelect.required = true;
      timeSelect.dispatchEvent(new Event('update'));

      return;
    }

    timeSelect.disabled = true;
    timeSelect.required = false;
    timeSelect.dispatchEvent(new Event('update'));
  });
};

/**
 * Converts UTC appointment times to user's local timezone and adjusts the date grouping.
 * This ensures appointments are grouped by the correct day in the user's timezone,
 * not UTC.
 *
 * @param {Array} data - Array of appointment objects with start/end times in UTC
 * @returns {Array} Array with times converted to local timezone
 */
const convertAppointmentsToLocalTimezone = data => {
  if (!data?.result || !Array.isArray(data.result)) {
    return data;
  }

  const converted = data.result.map(appointment => {
    // Parse UTC times
    const startUTC = new Date(appointment.start);
    const endUTC = new Date(appointment.end);

    // Format local time to match the original pattern: YYYY-MM-DDTHH:mm:ss+offset
    const formatLocalDateTime = date => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      // Get timezone offset in format +HH:mm or -HH:mm
      const offset = -date.getTimezoneOffset();
      const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
      const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0');
      const offsetSign = offset >= 0 ? '+' : '-';

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
    };

    return {
      ...appointment,
      start: formatLocalDateTime(startUTC),
      end: formatLocalDateTime(endUTC)
    };
  });

  return {
    ...data,
    result: converted
  };
};

/**
 * loads the consultation calendar of the specified type into select-inputs
 * @param {string} type
 * @param {HTMLElement} dateSelect
 * @param {HTMLElement} timeSelect
 * @param {boolean} noDayOption
 * @returns
 */
const createCalendarLoader =
  (span, endpoint, service, daysinTheFuture) =>
  (type, dateSelect, timeSelect, noDayOption = true) => {
    //reset the inputs
    resetSelect(dateSelect);
    resetSelect(timeSelect);
    timeSelect.disabled = true;
    timeSelect.required = false;
    timeSelect.dispatchEvent(new Event('update'));
    dateSelect.dispatchEvent(new Event('update'));

    return new Promise((resolve, reject) => {
      startSpan(span, 'http.client', { calendar: type }, () => fetch(`${service}${endpoint}?calendar=${type}${daysinTheFuture ? `&days=${daysinTheFuture}` : ''}`))
        .then(response => {
          if (!response.ok) {
            throw new Error('Error fetching appointment data: ' + response.status);
          }
          return response.json();
        })
        .then(data => {
          const localData = convertAppointmentsToLocalTimezone(data);
          fillAppointment(localData, dateSelect, timeSelect, noDayOption);
          resolve(data);
        })
        .catch(error => {
          captureException(error, { extra: { calendar: type } });
          reject(error);
        });
    });
  };

export const loadCalendar = createCalendarLoader('consultation.calendar.load', '/consultation/available-slots', window.craftConfig.global.leadServiceUrl);
export const loadInternalCalendar = createCalendarLoader('appointment-internal.calendar.load', '/appointments/internal/available-slots', window.craftConfig.global.appointmentServiceUrl, 14);

export const loadUserCalendar = (type, dateSelect, timeSelect, email, accessToken, noDayOption = true) => {
  //reset the inputs
  resetSelect(dateSelect);
  resetSelect(timeSelect);
  timeSelect.disabled = true;
  timeSelect.required = false;
  timeSelect.dispatchEvent(new Event('update'));
  dateSelect.dispatchEvent(new Event('update'));

  return new Promise((resolve, reject) => {
    startSpan('appointment-user.calendar.load', 'http.client', { calendar: type }, () =>
      fetch(`${window.craftConfig.global.appointmentServiceUrl}/appointments/user/available-slots?calendar=${type}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_token: accessToken,
          email: email
        })
      })
    )
      .then(response => {
        if (!response.ok) {
          throw new Error('Error fetching appointment data: ' + response.status);
        }
        return response.json();
      })
      .then(data => {
        const localData = convertAppointmentsToLocalTimezone(data);
        fillAppointment(localData, dateSelect, timeSelect, noDayOption);
        resolve(data);
      })
      .catch(error => {
        captureException(error, { extra: { calendar: type } });
        reject(error);
      });
  });
};
